import { useState } from "react";
import { Instagram, Loader2, Unplug, Webhook, IdCard, FileText } from "lucide-react";
import { toast } from "react-toastify";
import { desconectarConta } from "../services/instagramApi";

export default function ContaCard({ conta, onDisconnected }) {
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);

  const ativo = conta.status === "ativo";

  async function handleDisconnect() {
    setLoading(true);
    try {
      await desconectarConta(conta.id);
      toast.success("Conta desconectada");
      onDisconnected?.();
    } catch (err) {
      toast.error("Falha ao desconectar: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
      setConfirming(false);
    }
  }

  const dataCriacao = conta.data_criacao
    ? new Date(conta.data_criacao).toLocaleString("pt-BR")
    : null;

  return (
    <div className="surface-card surface-card-hover p-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <span className="h-11 w-11 flex-shrink-0 rounded-xl bg-brand-500/15 ring-1 ring-brand-500/25 grid place-items-center">
            <Instagram className="h-5 w-5 text-brand-300" strokeWidth={2.2} />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-2xl font-semibold text-ink-1 truncate">
              @{conta.username || "—"}
            </h2>
            <p className="text-sm text-ink-3 truncate">{conta.name || "Sem nome"}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {ativo ? (
            <span className="chip-positive">Ativo</span>
          ) : (
            <span className="chip-neutral">{conta.status || "—"}</span>
          )}
          {conta.webhook_subscribed && (
            <span className="chip-brand">
              <Webhook className="h-3 w-3" /> Webhook ativo
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <Stat icon={<IdCard className="h-4 w-4" />} label="IG User ID" value={conta.ig_user_id} mono />
        <Stat icon={<FileText className="h-4 w-4" />} label="Page ID" value={conta.page_id} mono />
      </div>

      {dataCriacao && (
        <p className="text-xs text-ink-4 mb-4">Conectado em {dataCriacao}</p>
      )}

      {!confirming ? (
        <button onClick={() => setConfirming(true)} className="btn-ghost">
          <Unplug className="h-4 w-4" /> Desconectar
        </button>
      ) : (
        <div className="rounded-xl border border-danger-500/30 bg-danger-500/10 p-3 flex flex-wrap items-center gap-3">
          <span className="text-sm text-danger-400">
            Tem certeza? A Meta vai parar de enviar eventos dessa conta.
          </span>
          <button
            onClick={handleDisconnect}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg bg-danger-500/90 hover:bg-danger-500 disabled:opacity-60 disabled:cursor-not-allowed text-white px-3 py-1.5 text-sm font-medium transition-colors"
          >
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Sim, desconectar
          </button>
          <button
            onClick={() => setConfirming(false)}
            disabled={loading}
            className="text-sm text-ink-3 hover:text-ink-1 transition-colors"
          >
            Cancelar
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value, mono }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-surface-3/40 p-3">
      <div className="flex items-center gap-1.5 text-xs text-ink-4 mb-1">
        {icon} {label}
      </div>
      <div className={`text-sm text-ink-1 truncate ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
